/**
 * Deezer access layer. No React imports.
 *
 * WHY JSONP (the whole reason this file is interesting):
 * Deezer's REST API responds with `Access-Control-Allow-Methods`, `-Headers`,
 * `-Credentials` and `-Expose-Headers` but NOT `Access-Control-Allow-Origin`.
 * That looks CORS-enabled at a glance and is not: without ACAO the browser
 * blocks every `fetch`, from every origin, including https ones. Probed
 * 2026-09-23 (see DECISIONS.md).
 *
 * Deezer's own JSONP mode (`&output=jsonp&callback=<fn>`) returns
 * `content-type: text/javascript`, a valid script MIME, so `nosniff` does not
 * block it and it loads through `<script>`. That keeps the request between
 * the browser and Deezer — unlike a public CORS proxy, which would also see
 * every mood the user types, and which was rejected on availability grounds
 * anyway (two of the three candidates were returning Cloudflare 522).
 */
import { fetchJsonp } from '../http';
import type { Track } from './types';

const API = 'https://api.deezer.com';
const LIMIT = 25;

/* ------------------------------------------------------------------ */
/* Wire types — `unknown`-narrowed, because a JSONP payload is a script */
/* we executed: it must be validated, never trusted to have a shape.    */
/* ------------------------------------------------------------------ */

interface DeezerError {
  readonly type?: string;
  readonly message?: string;
  readonly code?: number;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v : null;
}

function nested(v: unknown, key: string): Record<string, unknown> | null {
  if (!isRecord(v)) return null;
  const inner = v[key];
  return isRecord(inner) ? inner : null;
}

/**
 * Normalise one raw Deezer track. Returns null for anything unusable.
 *
 * `preview` is taken VERBATIM. It is a signed URL (`hdnea=exp=...`) on a CDN
 * host that is not stable across responses, so rebuilding it from a
 * hardcoded host pattern — which several tutorials do — breaks silently.
 */
export function toTrack(raw: unknown, source: 'search' | 'chart'): Track | null {
  if (!isRecord(raw)) return null;

  const id = raw['id'];
  const title = str(raw['title_short']) ?? str(raw['title']);
  const preview = str(raw['preview']);
  if (title === null || preview === null) return null;
  if (typeof id !== 'number' && typeof id !== 'string') return null;

  const artistName = str(nested(raw, 'artist')?.['name']) ?? 'Unknown artist';
  const album = nested(raw, 'album');
  const duration = raw['duration'];

  return {
    id: String(id),
    title,
    artist: artistName,
    album: str(album?.['title']),
    artwork: str(album?.['cover_medium']) ?? str(album?.['cover']),
    preview,
    link: str(raw['link']),
    // Deezer reports the FULL track length; the playable preview is 30s.
    durationSec: typeof duration === 'number' && duration > 0 ? Math.min(30, duration) : 30,
    source,
  };
}

/**
 * Pick the track to play from a result list. PURE — no network, no React.
 * This is the piece worth unit-testing, so it takes an array and returns one.
 *
 * The hard rule: a track whose `preview` is empty cannot be played, so it is
 * never chosen over one that can be, no matter how well it ranks. (`toTrack`
 * already drops those, but this function is the one with the invariant, and
 * it must hold if it is ever called with a list from somewhere else.)
 *
 * Beyond that, relevance order carries most of the signal, so earlier wins;
 * artwork is the tiebreak, because a result with cover art is far more often
 * the real release than the karaoke/tribute re-recordings Deezer likes to
 * interleave, and the card looks better with it.
 */
export function pickBestTrack(tracks: readonly Track[]): Track | null {
  let best: Track | null = null;
  let bestScore = -Infinity;
  tracks.forEach((t, i) => {
    const score =
      (t.preview.trim() !== '' ? 1_000 : 0) + (t.artwork !== null ? 1 : 0) - i * 2;
    if (score > bestScore) {
      bestScore = score;
      best = t;
    }
  });
  return best;
}

export type DeezerOutcome =
  | { kind: 'ok'; tracks: readonly Track[] }
  | { kind: 'empty' }
  | { kind: 'failed'; error: string };

function readList(payload: unknown, source: 'search' | 'chart'): DeezerOutcome {
  if (!isRecord(payload)) return { kind: 'failed', error: 'Malformed response' };

  // Deezer reports API-level errors INSIDE a 200 JSONP payload.
  const err = payload['error'] as DeezerError | undefined;
  if (isRecord(err)) {
    const quota = err.code === 4 || err.type === 'Exception';
    return { kind: 'failed', error: quota ? 'Deezer is rate-limiting requests' : 'Deezer rejected the search' };
  }

  // /search returns { data: [...] }; /chart/0/tracks returns { data: [...] }
  // too, but /chart (unsuffixed) nests under `tracks.data`. Handle both.
  const listRaw = Array.isArray(payload['data'])
    ? payload['data']
    : nested(payload, 'tracks')?.['data'];
  if (!Array.isArray(listRaw)) return { kind: 'failed', error: 'Malformed response' };

  const tracks = listRaw
    .map((r) => toTrack(r, source))
    .filter((t): t is Track => t !== null);

  return tracks.length === 0 ? { kind: 'empty' } : { kind: 'ok', tracks };
}

export async function searchDeezer(query: string, timeoutMs = 8_000): Promise<DeezerOutcome> {
  const url = `${API}/search?q=${encodeURIComponent(query)}&limit=${LIMIT}&output=jsonp`;
  const res = await fetchJsonp<unknown>(url, { timeoutMs });
  if (!res.ok) return { kind: 'failed', error: res.error };
  return readList(res.data, 'search');
}

/** Second step of the chain: something popular, when the search found nothing. */
export async function fetchDeezerChart(timeoutMs = 8_000): Promise<DeezerOutcome> {
  const url = `${API}/chart/0/tracks?limit=${LIMIT}&output=jsonp`;
  const res = await fetchJsonp<unknown>(url, { timeoutMs });
  if (!res.ok) return { kind: 'failed', error: res.error };
  return readList(res.data, 'chart');
}
